import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, AlertTriangle, ArrowRight, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
const DISMISS_KEY = 'hud_pending_mat_dismissed_v1';

/**
 * GenerationPendingNotice — banner Gold/Orange affiché en tête de la page
 * /generation quand au moins 1 projet a un CCTP en attente d'intégration.
 *
 * Behaviour:
 *   - Cherche les projets de l'utilisateur ayant `pending_materialtheque_job_id`
 *   - Affiche une carte HUD (Gold) listant les projets concernés + CTA "Vérifier"
 *   - L'utilisateur peut ignorer (mémorisé en localStorage par projet)
 */
const GenerationPendingNotice = () => {
  const [pendings, setPendings] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'); } catch { return []; }
  });

  useEffect(() => {
    const load = async () => {
      try {
        // Liste des jobs visibles (own + same-org), filtre ceux liés en pending
        const r = await axios.get(`${API}/materialtheque/jobs`, opts);
        const linkedPending = (r.data || []).filter((j) => j.project_id && j.linked_pending_at && !j.approved_at);
        // Pour chaque job, vérifier que le projet a toujours pending
        const projectIds = [...new Set(linkedPending.map((j) => j.project_id))];
        const projects = await Promise.all(projectIds.map(async (pid) => {
          try {
            const p = await axios.get(`${API}/materialtheque/projects/${pid}/pending`, opts);
            if (!p.data?.has_pending) return null;
            return {
              project_id: pid,
              file_name: p.data.draft?.source_file_name,
              total: p.data.draft?.summary?.total ?? 0,
              since: p.data.pending_since,
            };
          } catch { return null; }
        }));
        setPendings(projects.filter(Boolean));
      } catch {}
    };
    load();
  }, []);

  const handleDismiss = (pid) => {
    const next = [...new Set([...dismissed, pid])];
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch {}
  };

  const visible = pendings.filter((p) => !dismissed.includes(p.project_id));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2.5 mb-5 hud-fade-in" data-testid="generation-pending-notice">
      {visible.map((p) => (
        <div
          key={p.project_id}
          className="relative overflow-hidden rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(217,160,91,0.14) 0%, rgba(234,179,8,0.06) 100%)',
            border: '1px solid var(--c-border-accent)',
            padding: 16,
            boxShadow: '0 0 28px rgba(217,160,91,0.18)',
          }}
        >
          {/* Glow halo */}
          <span
            aria-hidden
            className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full"
            style={{ background: 'rgba(217,160,91,0.22)', filter: 'blur(50px)' }}
          />
          <div className="relative z-[1] flex items-center gap-4 flex-wrap">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'var(--c-gold)', boxShadow: '0 0 18px rgba(217,160,91,0.45)' }}
            >
              <AlertTriangle className="w-5 h-5 text-black" strokeWidth={2.25} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="hud-eyebrow hud-eyebrow-gold">Configuration en attente</span>
                <span className="hud-badge hud-badge-warning">
                  <Sparkles className="w-3 h-3" />
                  {p.total} matériaux
                </span>
              </div>
              <p
                className="mt-1 font-semibold tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 15.5 }}
              >
                Un nouveau document a été chargé pour ce projet — intégration au prompt requise
              </p>
              <p className="mt-0.5 text-[12.5px]" style={{ color: 'var(--c-text-2)' }}>
                <span className="hud-tabular">{p.file_name || 'CCTP'}</span> · Les générations utilisent l'ancien prompt jusqu'à validation
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`/projects/${p.project_id}?section=production`}>
                <button
                  className="hud-btn hud-btn-primary hud-btn-sm"
                  data-testid={`generation-pending-cta-${p.project_id}`}
                >
                  Vérifier & intégrer
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </Link>
              <button
                onClick={() => handleDismiss(p.project_id)}
                aria-label="Ignorer pour cette session"
                className="hud-btn hud-btn-ghost hud-btn-sm aspect-square px-0"
                data-testid={`generation-pending-dismiss-${p.project_id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GenerationPendingNotice;
